'use client';

import { useEffect, useRef } from 'react';
import type { RawHtmlBlockProps } from '../types';
import { embedScriptUrl } from '../rawHtmlEmbeds';

/**
 * The ONLY block that injects HTML. Writing it is limited to staff and people with HTML
 * access (gateway lib/html-access.ts), and every surface sanitizes it before it gets here:
 * the live site with tenant-sites lib/sanitize.ts, the Studio with builder lib/sanitizeBlocks.ts.
 * Native blocks must never use dangerouslySetInnerHTML.
 *
 * Embed scripts (Dawn, 2026-10-20: "allowed embeds live"). The live sanitizer keeps an empty
 * `<script src>` only from a listed embed host (rawHtmlEmbeds). HTML set through innerHTML
 * never runs its scripts, and server-rendered HTML would run them on load and then again on a
 * client navigation, so the tags are taken out of the markup here and added once from an
 * effect. The host is checked again, so nothing unlisted ever runs, whatever reaches this.
 */
const SCRIPT_TAG = /<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>\s*<\/script>/gi;

// The builder turns this off at start-up (apps/builder src/main.tsx): its pages are on
// breadlist.site, where no section's script should ever run, staff-made looks included.
let embedScriptsEnabled = true;
export function setEmbedScriptsEnabled(on: boolean): void {
  embedScriptsEnabled = on;
}

export function splitEmbedScripts(html: string): { markup: string; scripts: string[] } {
  const scripts: string[] = [];
  const markup = html.replace(SCRIPT_TAG, (_m, src: string) => {
    // Deduped by the full normalised URL (host case and a :443 port folded by `URL`). A
    // different query string is a different script and loads separately.
    const url = embedScriptUrl(src.replace(/&amp;/g, '&'));
    if (url && !scripts.includes(url)) scripts.push(url);
    return '';
  });
  // Any other <script> left in the string is inert under innerHTML; drop it so it can't run
  // from server-rendered HTML either.
  return { markup: markup.replace(/<script\b[\s\S]*?<\/script>/gi, ''), scripts };
}

export function RawHtmlBlock({ html }: RawHtmlBlockProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { markup, scripts } = splitEmbedScripts(html);
  const key = scripts.join('\n');

  useEffect(() => {
    const host = ref.current;
    if (!host || !key || !embedScriptsEnabled) return;
    for (const src of key.split('\n')) {
      if (embedScriptUrl(src) !== src) continue; // re-checked; only normalised listed URLs load
      // One copy per page: a second section with the same embed reuses the first script.
      const already = Array.from(document.querySelectorAll<HTMLScriptElement>('script[data-embed-src]')).some((el) => el.dataset.embedSrc === src);
      if (already) continue;
      const el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.dataset.embedSrc = src;
      host.appendChild(el);
    }
  }, [key]);

  // `blk-prose` is what makes the brand's link colour reach a tenant's own copy: the links in
  // here are the "inline links in your page copy" that `link_color` claims to control, and until
  // this class existed that claim was aspirational — the role was editable and painted nothing.
  return <div ref={ref} className="blk-prose" dangerouslySetInnerHTML={{ __html: markup }} />;
}
